import { Suspense, type ReactNode } from 'react';
import { lazyChunk } from '@/lib/lazy-chunk';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { Spinner } from '@vnetwork/ui/primitives';
import { organizations } from '@/app/register-organizations';
import { ServicesNavProvider } from '@/app/ServicesNavProvider';
import { AppLayout } from '@/components/layout/AppLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { ProtectedRoute } from '@/routes/ProtectedRoute';

import LoginPage from '@/pages/auth/LoginPage';
import CallbackPage from '@/pages/auth/CallbackPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import InvitationPage from '@/pages/auth/InvitationPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import MigrationPage from '@/pages/auth/MigrationPage';
import ReconciliationPage from '@/pages/auth/ReconciliationPage';
import MaintenancePage from '@/pages/auth/MaintenancePage';
import ImpersonationPage from '@/pages/auth/ImpersonationPage';
import NotFoundPage from '@/pages/NotFoundPage';

/**
 * The signed-in areas are loaded on demand; `/auth/*` and `NotFoundPage` are not, because
 * they are the first thing a signed-out visitor renders and a chunk round trip there would
 * show a spinner before the login form.
 *
 * `ProtectedRoute` renders this tree's elements, so one <Suspense> wraps the router's
 * outlets (see `routeSuspense` below) rather than each element.
 */
const OverviewPage = lazyChunk(() => import('@/pages/OverviewPage'));
const AccountPage = lazyChunk(() => import('@/pages/account/AccountPage'));
const ServicesPage = lazyChunk(() => import('@/pages/services/ServicesPage'));
const SupportPage = lazyChunk(() => import('@/pages/support/SupportPage'));
const ChangelogPage = lazyChunk(() => import('@/pages/changelog/ChangelogPage'));
const ResourcesDiscoverPage = lazyChunk(() => import('@/pages/resources/ResourcesDiscoverPage'));
const InvitationReviewPage = lazyChunk(() =>
  organizations.pages.load().then((m) => ({ default: m.InvitationReviewPage }))
);
const JoinTeamPage = lazyChunk(() => organizations.pages.load().then((m) => ({ default: m.JoinTeamPage })));
// The full-window console popup is outside `ServicesPage`, so `service-modules.ts` never runs for it:
// register the VMware and Compute console loaders (`register-vmware`, `register-compute`) before the
// popup's chunk is requested.
const ConsoleRouter = lazyChunk(() =>
  Promise.all([import('@/app/register-vmware'), import('@/app/register-compute')])
    .then(() => import('@/pages/services/console-router'))
    .then((m) => ({ default: m.ConsoleRouter })),
);
const PayPalCallback = lazyChunk(() => import('@/pages/account/billing/PayPalCallback'));
const OAuthCallbackPage = lazyChunk(() => import('@/pages/oauth/OAuthCallbackPage'));

import { paths } from '@/app/paths';

/**
 * Preserves teamId when redirecting /teams/:teamId → organizations console.
 * Plain <Navigate> cannot read route params — requires a component.
 */
function TeamDetailRedirect() {
  const { teamId } = useParams<{ teamId: string }>();
  return <Navigate to={`${paths.organizations.base}/teams/${teamId ?? ''}`} replace />;
}

/**
 * Preserves projectId when redirecting /projects/:projectId → organizations console.
 */
function ProjectDetailRedirect() {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`${paths.organizations.base}/projects/${projectId ?? ''}`} replace />;
}

/** Shown while a signed-in area's chunk loads. */
function RouteFallback() {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Spinner />
    </div>
  );
}

/** Wraps a lazily loaded element so every route has its own boundary. */
const s = (element: ReactNode) => <Suspense fallback={<RouteFallback />}>{element}</Suspense>;

export const router = createBrowserRouter([
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { index: true, element: <Navigate to="/auth/login" replace /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'callback', element: <CallbackPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'invitation', element: <InvitationPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      // SSO auto-migration flow (entered from login when an email is migrationable)
      { path: 'migration', element: <MigrationPage /> },
      // Reconciliation deep-link landing (public, token-gated) — from đối soát emails
      { path: 'reconciliation', element: <ReconciliationPage /> },
      // Legacy @console slugs: the backend password-reset email links to
      // `/auth/resetpw?token=…` (and `/auth/forgotpw`). Alias them so existing
      // emails keep resolving in the new app.
      { path: 'forgotpw', element: <ForgotPasswordPage /> },
      { path: 'resetpw', element: <ResetPasswordPage /> },
      { path: 'maintenance', element: <MaintenancePage /> },
      { path: 'impersonation/:method', element: <ImpersonationPage /> },
      // Legacy @console contract: staff impersonation links are emitted as
      // `/auth/:method/impersonate?jwt=…` (segment order + keyword differ from the
      // route above). Alias it so existing links keep working.
      { path: ':method/impersonate', element: <ImpersonationPage /> },
    ],
  },
  {
    // Bare full-window console popup — intentionally outside AppLayout (no chrome).
    path: paths.compute.instanceConsole,
    element: (
      <ProtectedRoute>{s(<ConsoleRouter />)}</ProtectedRoute>
    ),
  },
  {
    // Bare PayPal return popup — outside AppLayout; captures the approved order.
    path: '/payment/paypal/callback',
    element: (
      <ProtectedRoute>{s(<PayPalCallback />)}</ProtectedRoute>
    ),
  },
  {
    // Generic OAuth callback — outside AppLayout; Azure (and future providers) redirect here.
    // Path must match the redirect_uri registered in Azure AD:
    //   https://partner.vnetwork.{vn,labs,dev}/oauth/callback
    //
    // PUBLIC (no ProtectedRoute) on purpose: this loads inside an OAuth popup that
    // has NO app session/token, so wrapping it in ProtectedRoute would bounce it to
    // the login screen and break the flow. The page does nothing sensitive — it only
    // relays the `code`+`state` query params back to the opener tab via postMessage
    // (origin-checked); the opener (already authenticated) performs the token
    // exchange. No auth is needed to read query params and postMessage.
    path: '/oauth/callback',
    element: s(<OAuthCallbackPage />),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <ServicesNavProvider>
          <AppLayout />
        </ServicesNavProvider>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: s(<OverviewPage />) },
      { path: 'account/*', element: s(<AccountPage />) },
      /**
       * /teams and /projects are now hosted inside the Organizations console.
       * These redirect routes preserve bookmarks and cross-console deep-links.
       * Detail routes use param-preserving redirect components (TeamDetailRedirect,
       * ProjectDetailRedirect) because plain <Navigate> cannot read route params.
       */
      { path: 'teams', element: <Navigate to={`${paths.organizations.base}/teams`} replace /> },
      /* Accept a pending team invitation. The page lives in this console (not the
       * Organizations console), so it must be matched before the teams→org redirect
       * below — otherwise `invitations` is read as a teamId and bounced to a
       * non-existent org team ("Team not found"). */
      { path: 'teams/invitations/:memberId', element: s(<InvitationReviewPage />) },
      /* Redeem a foundation team invitation or join link (`?token=`); without a token it lists
       * the invitations addressed to the signed-in person. Same ordering rule as above. */
      { path: 'teams/join', element: s(<JoinTeamPage />) },
      { path: 'teams/:teamId/*', element: <TeamDetailRedirect /> },
      { path: 'projects', element: <Navigate to={`${paths.organizations.base}/projects`} replace /> },
      { path: 'projects/:projectId/*', element: <ProjectDetailRedirect /> },
      { path: 'resources/discover', element: s(<ResourcesDiscoverPage />) },
      { path: 'services/*', element: s(<ServicesPage />) },
      { path: 'support/*', element: s(<SupportPage />) },
      { path: 'changelog', element: s(<ChangelogPage />) },
      { path: 'activity', element: <Navigate to={`${paths.organizations.base}/activity`} replace /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
], {
  future: {
    v7_relativeSplatPath: true,
  },
});

export default router;
